import { createStore, applyMiddleware } from 'redux';
import thunk from 'redux-thunk';

import { StoreLocatorService } from './store-locator-service';
import { positionInBounds, getPosition } from './map-utils';

const storeLocatorService = new StoreLocatorService();

const initialState = {
  origin: null,
  stores: [],
  bounds: null,
  adjustVisibleStores: false,
  error: null
};

const rootReducer = (state = initialState, action) => {
  let stores;

  //console.log(`MAP ${action.type} ${JSON.stringify(action.payload)}`)
  switch (action.type) {
    case 'SET_ORIGIN':
      return {
        ...initialState,
        origin: action.payload.origin
      };
    case 'SET_ERROR':
      return {
        ...initialState,
        error: action.payload.error
      };
    case 'SET_STORES':
      return {
        ...state,
        stores: prepareStores(action.payload.stores, state.stores, action.payload.init),
        bounds: action.payload.bounds,
        adjustVisibleStores: action.payload.init
      };
    case 'SET_VISIBLE_STORES':
      return {
        ...state,
        bounds: action.payload.bounds
      };
    case 'SET_SELECTED_STORE':
      stores = state.stores.map(store =>
        store.id === action.payload.storeId || store.selected ?
          Object.assign({}, store, {selected: store.id === action.payload.storeId}) :
          store
      );
      return {
        ...state,
        stores: stores,
        adjustVisibleStores: false
      };
    default:
      return state;
  }
};

const setOrigin = (origin) => {
  return {
    type: 'SET_ORIGIN',
    payload: {
      origin: origin
    }
  };
};

const setError = (errorMessage) => {
  return {
    type: 'SET_ERROR',
    payload: {
      error: errorMessage
    }
  };
};

const setStores = ({stores, init, bounds}) => {
  return {
    type: 'SET_STORES',
    payload: {
      stores: stores,
      init: init,
      bounds: bounds
    }
  };
};

const setVisibleStores = (bounds) => {
  return {
    type: 'SET_VISIBLE_STORES',
    payload: {
      bounds: bounds
    }
  };
};

const setSelectedStore = (storeId) => {
  return {
    type: 'SET_SELECTED_STORE',
    payload: {
      storeId: storeId
    }
  };
};

const setLocation = (postcode, isMobileDevice, filter) => {
  window.ga && window.ga('send', 'event', 'new store locator', 'location search', postcode);
  return function (dispatch, getState) {
    return storeLocatorService.getCoordinatesByPostcode(postcode)
      .then((location) => {
        dispatch(setOrigin(location));
        return dispatch(loadStores({
          center: getState().origin,
          init: true,
          isMobileDevice: isMobileDevice,
          filter: filter
        }));
      })
      .catch((error) => {
        if(error.response && error.response.status === 404) {
          let errorMessage = error.response.data && error.response.data.error ||
            "Sorry, we don't service this location, please try another"
          dispatch(setError(errorMessage));
        }
      });
  };
};

const setCurrentLocation = (isMobileDevice, filter) => {
  window.ga && window.ga('send', 'event', 'new store locator', 'geolocation click', null);
  return function (dispatch, getState) {
    return getPosition()
      .then((location) => {
        dispatch(setOrigin(location));
        return dispatch(loadStores({
          center: getState().origin,
          init: true,
          isMobileDevice: isMobileDevice,
          filter: filter
        }));
      })
      .catch(() => {
        dispatch(setOrigin(null));
      });
  };
};

const loadStores = ({ center, init, bounds, filter }) => {
  return function (dispatch, getState) {
    return storeLocatorService.getStores(center, center, filter)
      .then(stores => {
        const sortedStores = stores.sort((a, b) => a.distance - b.distance);
        dispatch(setStores({ stores: sortedStores, init, bounds }));
      });
  };
};

const getVisibleStores = (stores, bounds) => {
  if (bounds) {
    return stores.filter(store => positionInBounds(store, bounds));
  } else {
    return stores;
  }
}

const prepareStores = (stores, origStores, init) => {
  if (init) {
    return [...stores];
  } else {
    return stores.map(store => {
      let storeId = store.id;
      let origStore = origStores.find(store => store.id === storeId);
      return origStore && origStore.selected
        ? Object.assign({}, store, { selected: origStore.selected })
        : store;
    });
  }
};

const configureStore = (preloadedState) => {
  return createStore(
    rootReducer,
    preloadedState,
    applyMiddleware(thunk)
  );
}

export {configureStore, setOrigin, setStores, setVisibleStores, setSelectedStore,
  setLocation, setCurrentLocation, loadStores, getVisibleStores}
